// src/common/nativeRoute.js
import { hasNativeBridge, detectRuntime, Runtime } from "util/userAgent";
import { showAppOnly } from "util/appOnlyPrompt";
import * as nativeBridge from "common/nativeBridge";
import { getActiveTypeHeader, ensureLessonCustomer, notifyRoleRequired, LESSON_PROVIDE_ROLE_MESSAGE } from "common/activeTypeGate";

/**
 * 네이티브/웹 공통 진입 함수 + 브릿지 액션별 웹 폴백 경로 매핑 테이블.
 *
 * 배경: `if (hasNativeBridge() && openX()) return;` 형태는 앱이 아니면 아무 동작도 하지 않아
 * 순수 웹에서 버튼이 죽는다. 모든 진입점을 이 파일의 openNativeOrWeb() 으로 통일해
 * 앱은 네이티브 화면, 웹은 대체 라우트로 보낸다.
 *
 * 규칙(docs/native-web-fallback-rules.md)
 *  - 폴백 경로는 아래 WEB_FALLBACK 한 곳에서만 관리한다. 페이지에 하드코딩하지 않는다.
 *  - 웹 대체 화면이 없는 액션은 null 을 반환하고, 앱 설치 유도 안내 모달을 띄운다(AppOnlyModalHost).
 *
 * 사용:
 *   import { openNativeOrWeb } from "common/nativeRoute";
 *   openNativeOrWeb(openPublicProfile, { userId: t.id }, navigate);           // 권장(테이블 사용)
 *   openNativeOrWeb(openPublicProfile, { userId: t.id }, `/teachers/${t.id}`, navigate); // 경로 직접 지정
 *   openNativeOrWeb(openRoleOnboarding, {}, navigate, { onUnavailable: () => setWebRole(true) });
 */

/* ───────── 액션별 웹 폴백 경로 ─────────
   값 = (payload) => 경로 문자열 | null.
   null = 대응하는 웹 화면 없음(안내 처리). 새 웹 화면을 만들면 이 표만 고치면 된다. */
const WEB_FALLBACK = {
  /* 프로필 열람 */
  openPublicProfile: (p) =>
    p?.userId ? (p.mode === "dancer" ? `/dancers/${p.userId}` : `/teachers/${p.userId}${p?.src ? `?src=${p.src}` : ""}`) : null,
  openTeacherProfile: (p) => (p?.teacherId ? `/teachers/${p.teacherId}` : null),
  openAcademyProfile: (p) => (p?.academyId ? `/academies/${p.academyId}${p?.src ? `?src=${p.src}` : ""}` : null),
  openApplicantResume: (p) =>
    p?.applicationId
      ? (p?.subId
        ? `/subs/${p.subId}/applicants/${p.applicationId}`
        : (p?.jobId ? `/jobs/${p.jobId}/applicants/${p.applicationId}` : null))
      : null,

  /* 이력서·프로필 관리 */
  openResumeList: (p) => (p?.mode === "dancer" ? "/profileResume?as=dancer" : "/profileResume?as=teacher"),
  openResumeEdit: (p) => {
    const q = new URLSearchParams();
    q.set("as", p?.mode === "dancer" ? "dancer" : "teacher");
    if (p?.seekProfile === true || String(p?.seekProfile) === "1") q.set("seekProfile", "1");
    if (p?.resumeId) q.set("id", String(p.resumeId));
    const qs = q.toString();
    return `/resumeEdit${qs ? `?${qs}` : ""}`;
  },
  // 공개 범위 설정 — 웹도 네이티브(FieldVisibilityView)와 같은 항목별 화면(2026-11-03).
  openFieldVisibility: (p) => (p?.as === "DANCER" || p?.mode === "dancer" ? "/fieldVisibility?as=dancer" : "/fieldVisibility"),

  /* 채용·공고 */
  openMyJobPostings: () => "/myPostings",

  /* 견적 */
  openMyQuotes: () => "/myQuotes",
  openReceivedQuotes: (p) => (p?.quoteId ? `/myQuotes/${p.quoteId}${p?.r ? `?r=${p.r}` : ""}` : null),
  openQuoteBrowse: () => "/availableQuotes",
  openCastingStage: () => "/casting",
  openAutoQuoteTemplates: () => "/quoteSettings",
  openAutoQuoteHistory: (p) => (p?.month ? `/autoQuoteHistory?month=${p.month}` : "/autoQuoteHistory"),
  openQuoteWizard: (p) => {
    // 웹 견적 요청 폼(/quoteRequest). category·targetTeacherId 를 쿼리로 넘긴다.
    const q = new URLSearchParams();
    if (p?.category) q.set("category", String(p.category));
    if (p?.categoryId) q.set("category", String(p.categoryId));
    if (p?.targetTeacherId) q.set("teacherId", String(p.targetTeacherId));
    if (p?.teacherId) q.set("teacherId", String(p.teacherId));
    const qs = q.toString();
    return `/quoteRequest${qs ? `?${qs}` : ""}`;
  },

  /* 레슨 */
  openLessonCalendar: (p) => (p?.lessonId ? `/lessonCalendar?lessonId=${p.lessonId}` : "/lessonCalendar"),
  openLessonCreate: (p) => (p?.lessonId ? `/lessons/${p.lessonId}/edit` : "/lessons/create"),
  openLessonQuoteHub: () => "/lessonHub",
  // 예약 — 웹은 라우트가 아니라 레슨 상세의 시트로 처리한다(onUnavailable 사용) → 경로 없음
  openLessonBooking: () => null,
  // 예약 상세 — 앱은 네이티브 시트, 웹은 /reservations/:id (취소·회차변경 포함)
  openLessonReservationDetail: (p) => (p?.reservationId ? `/reservations/${p.reservationId}` : null),
  // 예약 시간 관리 — 앱은 네이티브, 웹은 /lessonSlots (반복 규칙 + 회차 출석)
  openLessonSlotManage: (p) => (p?.lessonId ? `/lessonSlots?lessonId=${p.lessonId}` : "/lessonSlots"),

  /* 공간대관 */
  // 앱은 네이티브 공간 상세, 웹은 /spaces/:id (date·fromReservation 은 쿼리로 전달)
  openSpaceDetail: (p) => {
    if (p?.spaceId == null) return null;
    const q = new URLSearchParams();
    if (p?.date) q.set("date", String(p.date));
    if (p?.fromReservation) q.set("fromReservation", "1");
    const qs = q.toString();
    return `/spaces/${p.spaceId}${qs ? `?${qs}` : ""}`;
  },

  /* ★ 2026-11-03 무용센터 양도양수·공간 대관 — 앱(새 버전)은 네이티브, 웹·구버전 앱은 아래 웹 화면. */
  openStudioTransfers: () => "/studio-transfers",
  openStudioTransferDetail: (p) => (p?.id != null ? `/studio-transfers/${p.id}${p?.then ? `?then=${p.then}` : ""}` : null),
  openStudioTransferEditor: (p) => (p?.id != null ? `/studio-transfers/${p.id}/edit` : "/studio-transfers/create"),
  openMyStudioTransfers: () => "/myStudioTransfers",
  openSpaces: () => "/spaces",
  openSpaceEditor: (p) => (p?.id != null ? `/spaces/${p.id}/edit` : "/spaces/create"),
  openMySpaces: () => "/mySpaces",
  openMySpaceReservations: () => "/mySpaceReservations",
  openMySpaceBookings: () => "/mySpaceBookings",

  /* 스튜디오 운영(회원·매출·일정) — 앱은 네이티브, 브라우저는 같은 구성의 웹 화면(2026-11-04).
     프로 멤버십 판정은 웹 라우트(StudioProGate)가 앱과 같은 기준으로 한다. */
  openStudioOps: () => "/studio",
  openStudioMembers: (p) => (p?.memberId != null ? `/studio/members/${p.memberId}` : "/studio/members"),
  openStudioSales: () => "/studio/sales",
  openStudioSchedule: () => "/studio/schedule",

  /* 학원-강사 소속 */
  openAcademyTeachers: () => "/my/academyTeachers",
  openAcademyInvites: () => "/my/academyInvites",

  /* 멤버십·이용권 */
  // 낱개 기능 상품은 판매하지 않는다 — 어떤 featureType 이 와도 멤버십 화면으로 보낸다.
  openMembership: () => "/membership/MEMBERSHIP",
  openMembershipPerformance: () => "/myMembership/performance",

  /* 후기 */
  // 후기 전체 — 앱은 네이티브 ReviewListView, 웹은 같은 구성의 /teachers/:id/reviews(2026-10-29)
  openReviewList: (p) => (p?.teacherId ? `/teachers/${p.teacherId}/reviews` : null),
  openReviewWrite: (p) => (p?.teacherId ? `/teachers/${p.teacherId}?review=1` : null),
  // 후기 수정 — 앱은 네이티브 시트, 웹은 경로가 아니라 TeacherReviews 의 같은 구성 바텀시트(onUnavailable)로 연다.
  openReviewEdit: () => null,

  /* 가입·회원유형 */
  openSignupTerms: (p) => {
    // 웹 약관 화면은 mode(signup|gate) 와 동의 후 복귀 경로가 쿼리로 필요하다.
    const mode = p?.mode === "gate" ? "gate" : "signup";
    const r = p?.redirect ? `&redirect=${encodeURIComponent(p.redirect)}` : "";
    return `/signupTerms?mode=${mode}${r}`;
  },
  openRoleOnboarding: () => null, // 웹은 WebRoleOnboarding 모달로 대체(onUnavailable 사용)
  openRoleVerification: () => null, // 서류 첨부는 네이티브 전용
  openRoleManage: () => "/my/memberType", // 웹 회원유형 관리(2026-08-22 신설, 구 /memberSwitch 대체)

  /* 기타 진입점 */
  openAddressSetup: () => "/addRegion",
  openNotifications: () => "/notifications",
  // 브라우저에는 네이티브 설정 화면이 없다 — 같은 기능의 웹 화면으로 보낸다.
  openNotificationSettings: () => "/notificationSettings",
  // 웹 채팅(Socket.IO) 신설 — 2026-08-22
  openChatRoom: (p) => (p?.roomId ? `/chat/${p.roomId}` : "/chat"),
  openChatList: () => "/chat",

  openImageViewer: () => null, // 웹은 인라인 ImageCarousel 사용
  openGallery: () => null, // 웹은 file input 사용
};

/* 웹 대체 화면이 없을 때 표시할 안내 문구(미지정 시 기본 문구) */
const APP_ONLY_MESSAGE = {
  openRoleVerification: "역할 인증 서류 첨부는 앱에서 이용할 수 있어요.",
};
const DEFAULT_APP_ONLY_MESSAGE = "이 기능은 앱에서 이용할 수 있어요.";

/* ───────── 무용수 레슨 활동 차단(2026-09-26) ─────────
   무용수 유형으로 활동 중이면 레슨 관련 화면을 네이티브·웹 어디로도 열지 않는다.
   진입점이 화면마다 흩어져 있어 여기 한 곳에서 막는다(서버도 같은 기준으로 403).
   - 고객(요청·예약): 일반 유형 전환 확인 → 전환하면 같은 화면을 이어서 연다.
   - 제공자(개설·시간관리·허브·견적 응답): 강사·학원 원장 유형 안내(역할 추가/전환 화면 유도). */
const LESSON_CUSTOMER_ACTIONS = ["openQuoteWizard", "openLessonBooking"];
const LESSON_PROVIDER_ACTIONS = [
  "openLessonCreate", "openLessonSlotManage", "openLessonQuoteHub", "openQuoteBrowse", "openAutoQuoteTemplates",
  "openAutoQuoteHistory",
];

/* ───────── 액션별 네이티브 구현 플랫폼 ─────────
   미기재 = iOS·AOS 양쪽 구현됨(대부분).
   ⚠️ simpleBridge 는 postMessage "전송 성공"만으로 true 를 돌려준다. 그래서 미구현 플랫폼에서
      호출하면 네이티브도 안 뜨고 웹 폴백으로도 못 넘어가 버튼이 그대로 죽는다
      (2026-08-13 학원 소속 메뉴에서 실제 발생).
      → 여기에 적어두면 미지원 플랫폼에서는 브릿지를 건너뛰고 바로 웹으로 보낸다. */
const NATIVE_PLATFORMS = {
  // 지금은 비어 있다 — 학원 소속(openAcademyTeachers/openAcademyInvites)과 공간 상세
  // (openSpaceDetail) 모두 2026-09-06 에 AOS 이식이 끝나 제한이 필요 없다.
  // 새로 한쪽에만 있는 네이티브 화면이 생기면 여기 다시 적을 것
  //  (예: openFoo: [Runtime.IOS_WEBVIEW] — util/userAgent 의 Runtime 을 다시 import).
};

/** 현재 런타임에서 이 액션의 네이티브 화면을 열 수 있는가 */
function nativeSupported(name) {
  const only = name ? NATIVE_PLATFORMS[name] : null;
  if (only && !only.includes(detectRuntime())) return false;

  // 최근에 추가된 브릿지는 구버전 WKWebView도
  // postMessage 전송은 성공시키므로, 기능 확인 없이 호출하면 화면도 폴백도 열리지 않는다.
  // 새 앱은 documentStart 에 지원 기능을 주입한다. 구버전 iOS는 동일 기능의 웹 폴백을
  // 사용하게 한다(Android 동작은 기존대로 유지).
  if (
    detectRuntime() === Runtime.IOS_WEBVIEW &&
    (name === "openNotifications" || name === "openNotificationSettings" || name === "openImageViewer" ||
      // 2026-10-13: 자동견적 발송 내역 — 구버전 iOS 앱엔 화면이 없어 버튼이 죽었다 → 표시 없으면 웹 페이지로.
      name === "openAutoQuoteHistory")
  ) {
    return window.__muyeonNativeCapabilities?.[name] === true;
  }
  // 2026-10-26 '캐스팅·무대' — iOS·AOS 둘 다 새 앱에만 있다. 앱이 nativeCastingStage 를 넣어 줄 때만 네이티브.
  if (name === "openCastingStage") return window.__muyeonNativeCapabilities?.nativeCastingStage === true;
  // ★ 2026-11-03 양도양수·공간 대관 — 새 앱(1.1.5+)만. 구버전은 postMessage 만 성공하고 화면이 안 뜨므로 표시가 있을 때만.
  if (["openStudioTransfers", "openStudioTransferDetail", "openStudioTransferEditor", "openMyStudioTransfers"].includes(name)) {
    return window.__muyeonNativeCapabilities?.nativeStudioTransfers === true;
  }
  //  공간 상세도 같은 표시가 있을 때만 — 예전 앱의 네이티브 공간 상세에는 새 예약·결제가 없다(웹 상세로).
  if (["openSpaces", "openSpaceDetail", "openSpaceEditor", "openMySpaces", "openMySpaceReservations", "openMySpaceBookings"].includes(name)) {
    return window.__muyeonNativeCapabilities?.nativeSpaceBooking === true;
  }
  return true;
}

/* payload 객체를 그대로 받지 않는 브릿지 함수의 인자 변환.
   openRoleVerification 은 { role, ownerName } 을 그대로 받도록 바뀌어 변환이 필요 없다. */
const ARG_ADAPTER = {};

/* 브릿지 함수 → 액션 이름 역참조(nativeBridge 의 export 이름 기준) */
const ACTION_NAME_BY_FN = (() => {
  const map = new Map();
  Object.keys(nativeBridge).forEach((name) => {
    const fn = nativeBridge[name];
    if (typeof fn === "function" && !map.has(fn)) map.set(fn, name);
  });
  return map;
})();

/** 브릿지 함수 또는 액션 이름 → 액션 이름 */
export function actionNameOf(action) {
  if (typeof action === "string") return action;
  if (typeof action === "function") return ACTION_NAME_BY_FN.get(action) || null;
  return null;
}

/**
 * 앱(웹뷰) 여부. 브릿지 전용 부수효과(채팅방 생성 등)를 웹에서 건너뛸 때만 쓴다.
 *  화면 전환은 openNativeOrWeb() 을 쓰고 이 함수를 직접 분기에 쓰지 않는다.
 */
export function isNativeApp() {
  return hasNativeBridge();
}

/**
 * iOS 앱(웹뷰) 여부 — 애플 전용 문구(아이폰 설정 > 구독 등)를 고를 때만 쓴다.
 *  결제 차단 분기는 isStoreApp() 을 쓴다(안드로이드 앱도 같은 이유로 막아야 한다).
 *  window.webkit 은 iOS WKWebView 에만 있다.
 */
export function isIosApp() {
  return typeof window !== "undefined" && !!window.webkit?.messageHandlers && hasNativeBridge();
}

/**
 * 안드로이드 앱(웹뷰) 여부 — AppBridge(또는 레거시 Android* 인터페이스) 존재로 판정(util/userAgent).
 *  ⚠️ 안드로이드에 window.webkit shim 을 만들지 말 것 — 웹 isIOS() 레거시 경로가 전부 하이재킹된다.
 */
export function isAndroidApp() {
  return detectRuntime() === Runtime.ANDROID_WEBVIEW;
}

/**
 * 스토어 앱(iOS·안드로이드) 여부 — 멤버십 결제 분기 전용.
 *  앱 안에서는 멤버십(앱 안 디지털 혜택)을 스토어 결제로만 팔 수 있다
 *  (App Store 심사 3.1.1 / Google Play 결제 정책). 토스 결제·가격 화면이나 웹 결제 링크를 보여주면
 *  반려·정책 위반이므로 네이티브 멤버십 화면(openMembership: 애플 인앱결제 / Google Play 결제)으로 보낸다.
 *  ※ 레슨 예약금 등 실물(오프라인) 서비스 결제는 스토어 결제 대상이 아니라 토스 그대로 둔다.
 */
/**
 * 앱이 이 기능을 지원한다고 표시했는가(window.__muyeonNativeCapabilities, 앱이 페이지마다 넣는다).
 *  같은 브릿지 액션이라도 앱 버전에 따라 화면 규칙이 다를 때 쓴다 — 표시가 없으면(구버전 앱) 웹으로.
 */
export function nativeHasCapability(name) {
  return typeof window !== "undefined" && window.__muyeonNativeCapabilities?.[name] === true;
}

export function isStoreApp() {
  return isIosApp() || isAndroidApp();
}

/** 웹 대체 화면이 없는 액션의 안내(설치 유도 모달)를 직접 띄운다. */
export function notifyAppOnly(action, message) {
  const name = actionNameOf(action);
  showAppOnly(message || APP_ONLY_MESSAGE[name] || DEFAULT_APP_ONLY_MESSAGE);
}

/** 액션 + payload → 웹 폴백 경로(없으면 null) */
export function webFallbackPath(action, payload = {}) {
  const name = actionNameOf(action);
  const build = name ? WEB_FALLBACK[name] : null;
  if (!build) return null;
  try {
    return build(payload) || null;
  } catch (e) {
    return null;
  }
}

/** 웹 대체 화면 존재 여부(웹에서 진입 버튼 노출 판단용) */
export function hasWebFallback(action, payload = {}) {
  return !!webFallbackPath(action, payload);
}

/**
 * 앱이면 네이티브 화면, 웹이면 폴백 경로로 이동한다.
 *
 * @param {Function|string} action  nativeBridge 의 open* 함수(또는 액션 이름)
 * @param {object} payload          브릿지에 전달할 값(폴백 경로 계산에도 사용)
 * @param {...any} rest             navigate 함수 / 폴백 경로 문자열(테이블 대신 직접 지정) / 옵션 객체
 *                                  옵션: { onUnavailable, message, replace, silent }
 * @returns {boolean}               네이티브 실행 또는 웹 이동으로 처리되면 true
 */
export function openNativeOrWeb(action, payload = {}, ...rest) {
  let navigate = null;
  let webPath = null;
  let options = {};
  rest.forEach((arg) => {
    if (typeof arg === "function") navigate = arg;
    else if (typeof arg === "string") webPath = arg;
    else if (arg && typeof arg === "object") options = arg;
  });

  // 0) 무용수 활동 중 레슨 진입 차단(위 LESSON_*_ACTIONS 참고)
  if (getActiveTypeHeader() === "DANCER") {
    const name = actionNameOf(action);
    if (LESSON_CUSTOMER_ACTIONS.includes(name)) {
      ensureLessonCustomer().then((ok) => { if (ok) openNativeOrWeb(action, payload, ...rest); });
      return true;
    }
    if (LESSON_PROVIDER_ACTIONS.includes(name)) {
      notifyRoleRequired({ code: "ROLE_REQUIRED", message: LESSON_PROVIDE_ROLE_MESSAGE });
      return true;
    }
  }

  // 1) 앱(웹뷰) — 네이티브 화면 우선. 브릿지가 false 를 주면 웹 폴백으로 넘어간다.
  const fn = typeof action === "function" ? action : nativeBridge[action];
  if (hasNativeBridge() && typeof fn === "function" && nativeSupported(actionNameOf(action))) {
    const adapt = ARG_ADAPTER[actionNameOf(action)];
    try {
      if (fn(adapt ? adapt(payload) : payload) !== false) return true;
    } catch (e) {
      /* 폴백 진행 */
    }
  }

  // 2) 웹 — 매핑 테이블(또는 호출부 지정 경로)로 이동
  const path = webPath || webFallbackPath(action, payload);
  if (path) {
    if (navigate) navigate(path, options.replace ? { replace: true } : undefined);
    else if (typeof window !== "undefined") window.location.assign(path);
    return true;
  }

  // 3) 웹 대체 화면 없음 — 호출부 처리(모달 등)가 있으면 위임, 없으면 앱 설치 유도 안내
  if (typeof options.onUnavailable === "function") {
    options.onUnavailable();
    return false;
  }
  if (!options.silent) {
    const name = actionNameOf(action);
    // 토스트 대신 설치 유도까지 붙은 안내 모달(AppOnlyModalHost).
    showAppOnly(options.message || APP_ONLY_MESSAGE[name] || DEFAULT_APP_ONLY_MESSAGE);
  }
  return false;
}

export default openNativeOrWeb;

/**
 * ★ 2026-10-23 앱 안에서 웹 채팅방(/chat/:id)이 열렸을 때 네이티브 채팅방으로 넘긴다.
 *  알림톡 [채팅하기](/app/chat/123)는 안드로이드에서 웹 경로로 열려 웹 채팅이 떴다(아이폰은 네이티브).
 *  웹 폴백이 없는 단방향 호출이라 openNativeOrWeb 를 쓰지 않는다(폴백이 같은 /chat/:id 라 무한 이동).
 *  넘겼으면 true.
 */
export function handOffChatRoomToNative(roomId) {
  if (!roomId || !hasNativeBridge() || !nativeSupported("openChatRoom")) return false;
  try {
    return nativeBridge.openChatRoom({ roomId }) !== false;
  } catch (e) {
    return false;
  }
}
